import { Link } from 'react-router-dom'
import VehicleCard from '../components/VehicleCard'
import VehicleGalleryLightbox from '../components/VehicleGalleryLightbox'
import VehicleImagePlaceholder from '../components/VehicleImagePlaceholder'
import VehicleDetailSidebar from './vehicleDetail/VehicleDetailSidebar'
import { useVehicleDetailPage } from './vehicleDetail/useVehicleDetailPage'
import './VehicleDetail.css'

function VehicleDetailHero({ vehicle, t, priceDisplay, backClass = 'vd__back--hero' }) {
  const badges = [
    vehicle.year != null && vehicle.year !== '' ? { key: 'year', label: String(vehicle.year) } : null,
    vehicle.mileage != null
      ? { key: 'km', label: `${Number(vehicle.mileage).toLocaleString('es-UY')} km` }
      : null,
    vehicle.condition ? { key: 'cond', label: vehicle.condition } : null,
  ].filter(Boolean)

  return (
    <header className="vd__hero">
      <div className="vd__hero-glow" aria-hidden />
      <div className="vd__hero-inner">
        <Link to="/vehiculos" className={`vd__back ${backClass}`}>
          ← {t('detail.back')}
        </Link>
        <h1 className="vd__hero-title">{vehicle.title}</h1>
        <div className="vd__hero-foot">
          <span className="vd__hero-price">{priceDisplay}</span>
          {badges.length > 0 ? (
            <div className="vd__hero-badges">
              {badges.map((b) => (
                <span key={b.key} className="vd__hero-badge">
                  {b.label}
                </span>
              ))}
            </div>
          ) : null}
        </div>
      </div>
    </header>
  )
}

function VehicleDetailGallery({
  images,
  mainIdx,
  setMainIdx,
  mainSrc,
  openGallery,
  t,
  variant = 'centered',
}) {
  const hasPhotos = images.length > 0 && mainSrc

  return (
    <div className={`vd__gallery vd__gallery--${variant}`}>
      {hasPhotos ? (
        <button
          type="button"
          className="vd__gallery-main"
          onClick={() => openGallery(mainIdx)}
          aria-label={t('detail.openGallery')}
        >
          <img src={mainSrc} alt="" />
          <span className="vd__gallery-counter">
            {mainIdx + 1} / {images.length}
          </span>
          <span className="vd__mosaic-zoom" aria-hidden>
            ⊕ {t('detail.openGallery')}
          </span>
          {images.length > 1 ? (
            <>
              <span
                role="button"
                tabIndex={0}
                className="vd__nav vd__nav--prev"
                onClick={(e) => {
                  e.stopPropagation()
                  setMainIdx((i) => (i === 0 ? images.length - 1 : i - 1))
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.stopPropagation()
                    e.preventDefault()
                    setMainIdx((i) => (i === 0 ? images.length - 1 : i - 1))
                  }
                }}
                aria-label={t('detail.prevImage')}
              >
                ‹
              </span>
              <span
                role="button"
                tabIndex={0}
                className="vd__nav vd__nav--next"
                onClick={(e) => {
                  e.stopPropagation()
                  setMainIdx((i) => (i === images.length - 1 ? 0 : i + 1))
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.stopPropagation()
                    e.preventDefault()
                    setMainIdx((i) => (i === images.length - 1 ? 0 : i + 1))
                  }
                }}
                aria-label={t('detail.nextImage')}
              >
                ›
              </span>
            </>
          ) : null}
        </button>
      ) : (
        <div className="vd__gallery-main vd__gallery-main--empty">
          <VehicleImagePlaceholder />
        </div>
      )}
    </div>
  )
}

function VehicleDetailBody({ t, summaryRows, aboutParagraphs }) {
  return (
    <>
      <section className="vd__summary">
        <h2 className="vd__h2">{t('detail.summary')}</h2>
        <div className="vd__summary-grid">
          {summaryRows.map((row) => (
            <div key={row.key} className="vd__summary-item">
              <span className="vd__summary-label">{row.label}</span>
              <span className="vd__summary-value">{row.value}</span>
            </div>
          ))}
        </div>
      </section>

      <div className="vd__article">
        <h2 className="vd__h2">{t('detail.aboutTitle')}</h2>
        {aboutParagraphs.map((p, i) => (
          <p key={i} className="vd__prose">
            {p}
          </p>
        ))}
      </div>
    </>
  )
}

function VehicleDetailSimilar({ t, similar }) {
  if (similar.length === 0) return null

  return (
    <section className="vd__similar" aria-label={t('detail.similarVehicles')}>
      <div className="vd__similar-inner">
        <div className="vd__similar-head">
          <h2 className="vd__similar-h">{t('detail.similarVehicles')}</h2>
          <Link to="/vehiculos" className="vd__similar-link">
            {t('detail.backToList')} →
          </Link>
        </div>
        <div className="vd__similar-grid">
          {similar.map((v) => (
            <VehicleCard key={v.id} vehicle={v} variant="modern" />
          ))}
        </div>
      </div>
    </section>
  )
}

function VehicleDetail() {
  const data = useVehicleDetailPage()
  const {
    t,
    vehicle,
    apiLoading,
    apiError,
    fromList,
    isFullscreen,
    images,
    mainIdx,
    setMainIdx,
    mainSrc,
    lightboxOpen,
    lightboxIdx,
    openGallery,
    handleLightboxIndex,
    closeGallery,
    advisorName,
    advisorEmail,
    advisorPhoto,
    contactPhoneLine,
    contactPhoneTel,
    whatsappLine,
    whatsappHref,
    onContactClick,
    summaryRows,
    similar,
    priceDisplay,
    aboutParagraphs,
  } = data

  const sidebarProps = {
    advisorName,
    advisorEmail,
    advisorPhoto,
    contactPhoneLine,
    contactPhoneTel,
    whatsappLine,
    whatsappHref,
    onContactClick,
  }

  if (apiLoading && !fromList) {
    return (
      <div className="vd vd--state">
        <div className="vd__state-card">
          <span className="vd__state-spinner" aria-hidden />
          <p>{t('proposal.loading')}</p>
          <Link to="/vehiculos" className="vd__link">
            {t('detail.backToList')}
          </Link>
        </div>
      </div>
    )
  }

  if (!vehicle || (apiError && !fromList)) {
    return (
      <div className="vd vd--state">
        <div className="vd__state-card">
          <span className="vd__state-icon" aria-hidden>
            ◇
          </span>
          <p>{t('detail.notFound')}</p>
          <Link to="/vehiculos" className="vd__link">
            {t('detail.backToList')}
          </Link>
        </div>
      </div>
    )
  }

  const lightbox = (
    <VehicleGalleryLightbox
      images={images}
      index={lightboxIdx}
      open={lightboxOpen}
      onIndexChange={handleLightboxIndex}
      onClose={closeGallery}
      title={vehicle.title}
    />
  )

  const galleryProps = {
    images,
    mainIdx,
    setMainIdx,
    mainSrc,
    openGallery,
    t,
  }

  const heroProps = { vehicle, t, priceDisplay }

  if (isFullscreen) {
    return (
      <div className="vd vd--fullscreen">
        <VehicleDetailHero {...heroProps} />
        <div className="vd__hero-full">
          <VehicleDetailGallery {...galleryProps} variant="hero" />
          {lightbox}
        </div>

        <div className="vd__container">
          <div className="vd__grid-2 vd__grid-2--hero">
            <div className="vd__main-col">
              <VehicleDetailBody t={t} summaryRows={summaryRows} aboutParagraphs={aboutParagraphs} />
            </div>
            <VehicleDetailSidebar {...sidebarProps} />
          </div>
        </div>

        <VehicleDetailSimilar t={t} similar={similar} />
      </div>
    )
  }

  return (
    <div className="vd vd--centered">
      <VehicleDetailHero {...heroProps} />

      <div className="vd__container">
        <div className="vd__top">
          <div className="vd__top-gallery">
            <VehicleDetailGallery {...galleryProps} variant="centered" />
            {lightbox}
          </div>
          <VehicleDetailSidebar {...sidebarProps} />
        </div>

        <VehicleDetailBody t={t} summaryRows={summaryRows} aboutParagraphs={aboutParagraphs} />
      </div>

      <VehicleDetailSimilar t={t} similar={similar} />
    </div>
  )
}

export default VehicleDetail
